import { defineComponent as _defineComponent } from 'vue'
import { PropType, ref, Ref, computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { Carousel, Slide, Pagination } from '@/lib/vue3-carousel/src'
import CarouselTrigger from '@/components/task/CarouselTrigger.vue'
import { playSounds, stopSounds } from '@/utilities'
import { useGameStore } from '@/store/useGameStore'
import { useDeviceService } from '@/composition/useDevice'
import { Horerduordet } from '@/templates/slplus/structure/questionModels'
import Button from '@/components/base/Button.vue'
import Avatar from '@/components/base/Avatar.vue'

import Intro from '@/components/task/Intro.vue'
import PlayersReady from '@/components/task/PlayersReady.vue'
import HorerQuizz from '@/components/task/horerduordet/HorerQuizz.vue'
import FindPlaces from '@/components/task/horerduordet/FindPlaces.vue'
import Instruction from '@/components/task/Instruction.vue'
import InstructionFinalPage from '@/components/task/horerduordet/InstructionFinalPage.vue'

import introImage from '@/assets/tasks/horerduordet/cover.svg'
import youpiImage from '@/assets/tasks/horerduordet/party.svg'

import trudeluttSound from '@/assets/tasks/stovnerfaret_26_10.mp3'
import introSound from '@/assets/tasks/horerduordet/HDO_intro.mp3'
import introSound2 from '@/assets/tasks/horerduordet/HDO_intro2.mp3'
import introSound3 from '@/assets/tasks/horerduordet/HDO_intro3.mp3'
import introSound4 from '@/assets/tasks/horerduordet/HDO_intro4.mp3'
import finishSound from '@/assets/tasks/horerduordet/742_hørerduordet_godlytt_Alex.mp3'
import lastIntroSound from '@/assets/tasks/horerduordet/HDO_start.mp3'

enum MODES {
  intro = 'intro',
  playersReady = 'playersReady',
  instruction = 'instruction',
  placeReview = 'placeReview',
  instructionReview = 'instructionReview',
  quizzIntro = 'quizzIntro',
  quizz = 'quizz',
  quizzDone = 'quizzDone',
  finished = 'finished',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Horerduordet',
  props: {
  question: { required: true, type: Object as PropType<Horerduordet> },
  myIndex: { required: false, type: Number, default: 0 },
},
  emits: ['start-introduction', 'completed', 'not-completed'],
  setup(__props, { expose, emit }) {
  expose();

const props = __props

const messages = {
  no: {
    title: 'Hører du ordet?',
    description: 'Hjelp Omar med å samle på hemmelige ord!',
    instructions: 'Instruksjoner',
    spill: 'Spill',
    exercise: 'Aktivitet ',
    yourTurn: 'La oss spille alle sammen',
    weReady: 'Neste',
    letsDiscuss: 'La oss alle snakke om det!',
    quizzTitle: 'Klar til å spille? Bytt plass når jeg sier ditt ord!',
    titleFinished: 'Jippi!',
    descriptionFinished: 'Dere er gode til å lytte etter ord! Takk for hjelpen!',
  },
  en: {
    title: 'Do you hear the word?',
    description: 'Help Omar to collect words!',
    instructions: 'Instructions',
    spill: 'Play',
    exercise: 'Exercise ',
    yourTurn: "Let's all play together",
    weReady: 'We are ready',
    letsDiscuss: "Let's all talk about it!",
    quizzTitle: 'Ready to play? Switch place when I say your word!',
    titleFinished: 'Jippi!',
    descriptionFinished: 'You are good at listening! Thanks for your help!',
  },
}




const { t } = useI18n({ messages })
const { question } = toRefs(props)
const mode: Ref<MODES> = ref(MODES.intro)
const savedRound = ref(0)
const { actions: deviceActions } = useDeviceService()
const gameStore = useGameStore()
let placesSeen = false
let savedState: Ref<{ mode: MODES; round: number } | undefined> = ref()
const exerciseIndex = computed(() => `${t('exercise')} ${props.myIndex + 1}`)
const bounce = ref(0)

const instructionPages = ref([
  {
    component: FindPlaces,
    audio: introSound3,
    description: '',
  },
  {
    component: HorerQuizz,
    audio: introSound4,
    description: '',
  },
  {
    component: Instruction,
    audio: question.value.instructionSound,
    description: 'Bra jobbet! La oss starte spillet nå!',
  },
  {
    component: InstructionFinalPage,
    audio: lastIntroSound,
    description: '',
  },
])

// Play the introduction audio automatically
emit('start-introduction', true)

// Review another state whilst allowing return to the current state
const reviewMode = (m: MODES, round: number): void => {
  savedState.value = { mode: mode.value, round: round }
  changeMode(m, true)
}

// Call this when the task is complete
const finish = async () => {
  stopSounds()
  await deviceActions.stopRecordingAudio()
  // Call this when the task is complete
  if (mode.value === MODES.finished) {
    emit('completed')
  }
  // Call when finishing early
  else emit('not-completed')
}

const playInstructionSound = (index: number): void => {
  stopSounds()
  setTimeout(() => {
    playSounds([instructionPages.value[index].audio])
  }, 200)
}

const alreadySeenInstruction = computed(() => localStorage.getItem('instSeen-horer') === 'true')

// ------ Functions to be called from parent -------

const changeMode = (newMode: MODES, reviewMode?: boolean): void => {
  stopSounds()
  // This section returns to a state after reviewing video, placement or instructions
  if (savedState.value && !reviewMode) {
    const m = savedState.value.mode
    savedRound.value = savedState.value.round
    savedState.value = undefined
    return changeMode(m)
  }

  switch (newMode) {
    case MODES.playersReady:
      playSounds([introSound2])
      // TODO: playSounds()
      break
    case MODES.quizzIntro:
      playInstruction()
      break
    case MODES.placeReview:
      playSounds([introSound3])
      if (placesSeen && !reviewMode) newMode = MODES.quizz
      else placesSeen = true
      break
    case MODES.instructionReview:
      playSounds([introSound4])
      bounce.value++
      break
    case MODES.instruction:
      playSounds([introSound3])
      break
    case MODES.quizz:
      bounce.value++
      localStorage.setItem('instSeen-horer', 'true')
      break
    case MODES.finished:
      playSounds([finishSound])
      break
  }
  mode.value = newMode
}

playSounds([trudeluttSound, introSound])

const playInstruction = () => {
  playSounds([question.value.instructionSound])
}

const players = gameStore.getters.playersInSelectedGame

const __returned__ = { messages, MODES, emit, props, t, question, mode, savedRound, deviceActions, gameStore, placesSeen, savedState, exerciseIndex, bounce, instructionPages, reviewMode, finish, playInstructionSound, alreadySeenInstruction, changeMode, playInstruction, players, Carousel, Slide, Pagination, CarouselTrigger, playSounds, stopSounds, Button, Avatar, Intro, PlayersReady, HorerQuizz, FindPlaces, introImage, youpiImage }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})